import { DateTime } from 'luxon';
import { ScreencastSessionStatus, ScreencastSessionSummary } from './screencast-sessions.types';

export type ScreencastDatePreset = 'today' | 'yesterday' | 'week' | 'month' | 'custom';

const END_REASON_LABELS: Record<string, string> = {
  admin_closed: 'Admin closed',
  page_closed: 'Page closed',
  mode_changed: 'Mode changed',
  employee_switched: 'Employee switched',
  next_patrol_employee: 'Next patrol employee',
  stale_timeout: 'Connection timed out',
  connection_failed: 'Connection failed',
  connection_timed_out: 'Connection timed out',
};

export function formatConnectedDuration(seconds: number | null | undefined, unavailable = false): string {
  if (unavailable || seconds === null || seconds === undefined) {
    return 'Unavailable';
  }
  const safe = Math.max(0, Math.floor(Number(seconds) || 0));
  const mins = Math.floor(safe / 60);
  const secs = safe % 60;
  if (mins <= 0) {
    return secs === 0 ? '0m' : `${secs}s`;
  }
  return secs ? `${mins}m ${secs}s` : `${mins}m`;
}

export function durationLabel(session: Pick<ScreencastSessionSummary, 'status' | 'duration_seconds' | 'duration_unavailable'>): string {
  if (session.status === 'Failed') {
    return '0m';
  }
  if (session.status === 'Connecting') {
    return 'Not connected';
  }
  if (session.status === 'Incomplete' || session.duration_unavailable) {
    return 'Unavailable';
  }
  return formatConnectedDuration(session.duration_seconds);
}

export function durationNote(session: Pick<ScreencastSessionSummary, 'status' | 'duration_is_provisional' | 'duration_is_estimated' | 'completeness' | 'today_contribution_seconds'>): string {
  if (session.duration_is_provisional && session.status === 'Live') {
    return 'Provisional';
  }
  if (session.status === 'Unknown') {
    return 'No recent activity';
  }
  if (session.duration_is_estimated) {
    return 'Estimated';
  }
  if (session.completeness?.crossed_midnight) {
    return `${formatConnectedDuration(session.today_contribution_seconds)} counted today`;
  }
  if (session.status === 'Incomplete') {
    return 'Incomplete record';
  }
  return '';
}

export function readableEndReason(reason: string | null | undefined, status?: ScreencastSessionStatus): string {
  const key = (reason || '').trim().toLowerCase();
  if (!key) {
    if (status === 'Live') {
      return 'Viewing in progress';
    }
    if (status === 'Connecting') {
      return 'Waiting for connection';
    }
    if (status === 'Unknown') {
      return 'No recent activity';
    }
    if (status === 'Incomplete') {
      return 'Connection event unavailable';
    }
    return '—';
  }
  if (END_REASON_LABELS[key]) {
    return END_REASON_LABELS[key];
  }
  // Sentence case — never shouty ALL CAPS from raw keys.
  const spaced = key.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function displayMode(mode: string | null | undefined): string {
  const value = (mode || 'unknown').trim();
  if (!value) {
    return 'Unknown';
  }
  return value.charAt(0).toUpperCase() + value.slice(1);
}

const EVENT_SOURCE_LABELS: Record<string, string> = {
  employee_modal: 'Employee profile',
  employee_list: 'Employee list',
  video_wall: 'Video wall',
  grid: 'Grid view',
  patrol: 'Patrol',
  single: 'Single view',
};

/** Human label for screencast launch source (audit UI). */
export function eventSourceLabel(source: string | null | undefined): string {
  const key = (source || '').trim();
  if (!key) {
    return '';
  }
  if (EVENT_SOURCE_LABELS[key]) {
    return EVENT_SOURCE_LABELS[key];
  }
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Timeline is for non-trivial stories. Clean Ended/Live sessions already
 * show times on the receipt — don't repeat Requested → Connected → Ended.
 */
export function shouldShowSessionTimeline(
  session: Pick<ScreencastSessionSummary, 'status'> | null | undefined,
  timeline: Array<{ label: string }> | null | undefined
): boolean {
  if (!session) {
    return false;
  }
  const status = session.status;
  if (status === 'Failed' || status === 'Dropped' || status === 'Incomplete' || status === 'Unknown') {
    return true;
  }
  const events = timeline || [];
  if (events.some((event) => event.label === 'Failed' || event.label === 'Dropped')) {
    return true;
  }
  return events.length > 3;
}

/** Show "Counted today" only when it differs from full duration (e.g. midnight cross). */
export function shouldShowCountedToday(
  session: Pick<
    ScreencastSessionSummary,
    'status' | 'duration_seconds' | 'today_contribution_seconds' | 'completeness'
  > | null | undefined
): boolean {
  if (!session) {
    return false;
  }
  if (session.status === 'Failed' || session.status === 'Connecting' || session.status === 'Incomplete') {
    return false;
  }
  if (session.completeness?.crossed_midnight) {
    return true;
  }
  const duration = Math.max(0, Math.floor(Number(session.duration_seconds) || 0));
  const today = Math.max(0, Math.floor(Number(session.today_contribution_seconds) || 0));
  return today > 0 && today !== duration;
}

export function initials(name: string | null | undefined): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) {
    return '?';
  }
  return parts.slice(0, 2).map((part) => part[0].toUpperCase()).join('');
}

export function formatClock(iso: string | null | undefined, timezone: string): string {
  if (!iso) {
    return '—';
  }
  const dt = DateTime.fromISO(iso, { zone: 'utc' }).setZone(timezone);
  if (!dt.isValid) {
    return '—';
  }
  return dt.toFormat('dd LLL yyyy, h:mm a');
}

export function formatExact(iso: string | null | undefined, timezone: string): string {
  if (!iso) {
    return '—';
  }
  const dt = DateTime.fromISO(iso, { zone: 'utc' }).setZone(timezone);
  if (!dt.isValid) {
    return '—';
  }
  return dt.toFormat('dd LLL, hh:mm:ss a');
}

export function presetDateRange(
  preset: ScreencastDatePreset,
  timezone: string,
  customStart?: string,
  customEnd?: string,
  now = DateTime.now()
): { start: string; end: string } {
  const local = now.setZone(timezone);
  const today = local.toISODate() || local.toFormat('yyyy-LL-dd');
  if (preset === 'custom') {
    return {
      start: customStart || today,
      end: customEnd || today,
    };
  }
  if (preset === 'yesterday') {
    const day = local.minus({ days: 1 }).toISODate() || today;
    return { start: day, end: day };
  }
  if (preset === 'week') {
    const start = local.minus({ days: 6 }).toISODate() || today;
    return { start, end: today };
  }
  if (preset === 'month') {
    const start = local.minus({ days: 29 }).toISODate() || today;
    return { start, end: today };
  }
  return { start: today, end: today };
}

export function isReversedDateRange(start: string, end: string): boolean {
  return !!start && !!end && start > end;
}

export function isNearQuotaLimit(usedSeconds: number | null, limitMinutes: number | null, remainingSeconds: number | null): boolean {
  if (limitMinutes == null || remainingSeconds == null || usedSeconds == null) {
    return false;
  }
  const limitSeconds = limitMinutes * 60;
  if (limitSeconds <= 0 || remainingSeconds <= 0) {
    return false;
  }
  return remainingSeconds <= Math.max(300, Math.round(limitSeconds * 0.1));
}

export function quotaAllowanceLabel(unlimited: boolean, limitMinutes: number | null): string {
  if (unlimited) {
    return 'Unlimited';
  }
  if (limitMinutes == null) {
    return '—';
  }
  return `${limitMinutes}m`;
}

export function statusClass(status: string): string {
  return (status || 'unknown').toLowerCase();
}
